import React, {useCallback, useEffect, useRef, useState} from 'react';
import '@react-awesome-query-builder/ui/css/styles.css';
import '@react-awesome-query-builder/ui/css/compact_styles.css';
import '@yaireo/tagify/dist/tagify.css'
import '../AlertForm.scss';
import AlertTextField from "features/alert-center/AlertForm/FormComponents/AlertTextField";
import HotelSelection from "features/alert-center/AlertForm/FormComponents/HotelSelection";
import RecipientsField from "features/alert-center/AlertForm/FormComponents/RecipientsField";
import {useParams} from "react-router-dom";
import {APP_ALERT_CENTER_API_ENDPOINT} from "../../../../constants/api_urls";
import CustomButtonWithIcon from "../../../../components/CustomButtonWithIcon/Buttons";
import {getUserToken} from "../../../../utils/functions/userToken";
import {getUserDetailsFromToken} from "../../../../utils/functions/userTokenDetails";
import TextField from "@mui/material/TextField";
import {InputAdornment, Typography} from "@mui/material";

const AlertGoogleSearchAdsBudget = ({alertData, actionType, onClose, showSnackbar, refreshData}) => {

    const {idaccount} = useParams();
    let method = 'POST';
    let apiActionEndpoint = '';
    if (actionType === 'edit') {
        method = 'PUT';
        apiActionEndpoint = `${APP_ALERT_CENTER_API_ENDPOINT}/api/alert-center-budget/google-search-ads/edit/${idaccount}/` + alertData.id;
    } else {
        apiActionEndpoint = `${APP_ALERT_CENTER_API_ENDPOINT}/api/alert-center-budget/google-search-ads/add/${idaccount}`;
    }
    const jwtToken = getUserToken();

    const [alertName, setAlertName] = useState(alertData?.name || '');
    const [alertRecipients, setAlertalertRecipients] = useState(alertData?.recipients || []);
    const [alertCondition, setAlertCondition] = useState(alertData?.alertCondition || '');
    const [alertSubject, setAlertSubject] = useState(alertData?.alertSubject || '');
    const tagifyRef = useRef(null);
    const [alertLevel, setAlertLevel] = useState(alertData?.alertLevel || "HOTEL");
    const [isHotelSlidingPanelOpen, setIsHotelSlidingPanelOpen] = useState(false);
    const [selectedHotels, setSelectedHotels] = useState([]);
    const [hotelRowSelection, setHotelRowSelection] = useState({});
    const [errorSelectedHotels, setErrorSelectedHotels] = useState(false);
    const [userDetails, setUserDetails] = useState({
        username: 'N/A',
        roles: 'N/A',
        isMainUser: false,
        isAdminUser: false,
        isSuperAdminUser: false
    });

    const [errors, setErrors] = useState({
        alertName: false,
        alertRecipients: false,
        alertCondition: false,
        alertSubject: false,
    });

    const handleRemoveHotel = (hotelId) => {
        setSelectedHotels(prevHotels => prevHotels.filter(h => h.id !== hotelId));
        setHotelRowSelection(prevSelection => {
            const newSelection = {...prevSelection};
            delete newSelection[hotelId];
            return newSelection;
        });
    };

    const handleClearAllHotels = () => {
        setSelectedHotels([]);
        setHotelRowSelection({});
    };

    const handleHotelSelect = useCallback((selectedHotels) => {
        setSelectedHotels(selectedHotels);
    }, []);


    useEffect(() => {
        const token = getUserToken();
        const {UserDetails} = getUserDetailsFromToken(token);
        setUserDetails(UserDetails);
    }, []);

    useEffect(() => {
        const fetchLinkedHotels = async () => {
            if (alertData && actionType === 'edit' && alertLevel === 'HOTEL') {
                try {
                    const res = await fetch(`${APP_ALERT_CENTER_API_ENDPOINT}/api/alert-center-metrics/list-linked-hotels/${alertData.id}`, {
                        method: 'GET',
                        headers: {
                            'Authorization': `Bearer ${jwtToken}`,
                            'Content-Type': 'application/json',
                        }
                    });
                    const hotels = await res.json();
                    setSelectedHotels(hotels);

                    const selectionMap = {};
                    hotels.forEach(hotel => {
                        selectionMap[hotel.id] = true;
                    });
                    setHotelRowSelection(selectionMap);
                } catch (error) {
                    console.error("Error fetching linked hotels:", error);
                }
            }
        };

        fetchLinkedHotels();
    }, [alertData, actionType, alertLevel]);


    const submitBudgetGoogleSearchAds = (requestBody) => {
        fetch(apiActionEndpoint, {
            method: method,
            headers: {
                'Authorization': `Bearer ${jwtToken}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestBody),
        })
            .then((response) => {
                if (!response.ok) { // Check if the response is not OK (status code outside 200-299)
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }
                return response.json();
            })
            .then((result) => {
                showSnackbar('Alert saved successfully', 'success');
                if (refreshData) {
                    refreshData();
                }
                onClose();
            })
            .catch((error) => {
                showSnackbar('Error saving alert' + error.message, 'error');

                const missingHotels = selectedHotels.length === 0;

                const newErrors = {
                    alertName: !requestBody.alert_name,
                    alertRecipients: requestBody.alert_recipients.length === 0,
                    alertCondition: !requestBody.alert_condition,
                    alertSubject: !requestBody.alert_subject,
                };
                setErrors(newErrors);
                setErrorSelectedHotels(missingHotels);
            });
    };


    const handleRecipientsChange = useCallback((e) => {
        let parsedValue;
        try {
            parsedValue = JSON.parse(e.detail.value);
        } catch (error) {
            console.error("Parsing error:", error);
            parsedValue = [];
        }

        const updatedRecipients = parsedValue.map(tag => tag.value);
        setAlertalertRecipients(updatedRecipients);
    }, []);

    useEffect(() => {
    }, [selectedHotels]);

    const handleSubmit = async () => {
        const body = {
            alert_name: alertName,
            alert_recipients: alertRecipients,
            alert_condition: alertCondition,
            alert_subject: alertSubject,
            alert_level: alertLevel,
            alert_hotels_selection: selectedHotels.map(hotel => hotel.id)
        };

        const missingHotels = alertLevel === "HOTEL" && selectedHotels.length === 0;

        const newErrors = {
            alertName: !body.alert_name,
            alertRecipients: body.alert_recipients.length === 0,
            alertCondition: !body.alert_condition,
            alertSubject: !body.alert_subject,
        };

        setErrors(newErrors);
        setErrorSelectedHotels(missingHotels);

        if (Object.values(newErrors).some(e => e) || missingHotels) {
            showSnackbar('Please fill in all required fields', 'error');
        } else {
            submitBudgetGoogleSearchAds(body);
        }
    };


    return (
        <div className="alert-form">
            <div className="top-side">
                <AlertTextField
                    placeholder="Alert Name:"
                    value={alertName}
                    onChange={(e) => setAlertName(e.target.value)}
                    error={errors.alertName}
                    helperText="This field is required"
                />
            </div>

            <div>
                <HotelSelection
                    isOpen={isHotelSlidingPanelOpen}
                    setOpen={setIsHotelSlidingPanelOpen}
                    selectedHotels={selectedHotels}
                    handleRemoveHotel={handleRemoveHotel}
                    handleClearAllHotels={handleClearAllHotels}
                    handleHotelSelect={handleHotelSelect}
                    hotelRowSelection={hotelRowSelection}
                    setHotelRowSelection={setHotelRowSelection}
                    idaccount={idaccount}
                    jwtToken={jwtToken}
                    showError={errorSelectedHotels}
                />
            </div>

            <TextField
                placeholder="Alert me when my budget has reached"
                variant="standard"
                type="number"
                value={alertCondition}
                onChange={(e) => setAlertCondition(e.target.value)}
                required
                error={errors.alertCondition}
                helperText={errors.alertCondition ? "This field is required" : ""}
                sx={{
                    '& ::placeholder': {fontSize: '12px'},
                    width: '55%',
                }}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <Typography variant="body2" sx={{fontSize: "12px", color: "#373F41"}}>
                                %
                            </Typography>
                        </InputAdornment>
                    ),
                }}
            />
            <AlertTextField
                placeholder="Subject"
                value={alertSubject}
                onChange={(e) => setAlertSubject(e.target.value)}
                error={errors.alertSubject}
                helperText="This field is required"
            />

            <RecipientsField
                value={alertRecipients}
                onChange={handleRecipientsChange}
                error={errors.alertRecipients}
            />
            <div className="button-container">
                <CustomButtonWithIcon
                    color="#b0acac"
                    hoverColor="#12794F"
                    titleColor="#373F41"
                    hoverTitleColor={"white"}
                    width={"120px"}
                    height={"24px"}
                    borderRadius="20px"
                    textStyle={{
                        fontFamily: 'Inter',
                        fontSize: '12px',
                        fontWeight: 500,
                        lineHeight: '14.52px',
                        textAlign: 'center',
                    }}
                    onClick={handleSubmit}

                >
                    Save Alert
                </CustomButtonWithIcon>

                <CustomButtonWithIcon
                    color="#b0acac"
                    hoverColor="#12794F"
                    titleColor="#373F41"
                    hoverTitleColor={"white"}
                    width={"120px"}
                    height={"24px"}
                    borderRadius="20px"
                    textStyle={{
                        fontFamily: 'Inter',
                        fontSize: '12px',
                        fontWeight: 500,
                        lineHeight: '14.52px',
                        textAlign: 'center',
                    }}
                    onClick={onClose}

                >
                    Cancel
                </CustomButtonWithIcon>
            </div>

        </div>
    );
};

export default AlertGoogleSearchAdsBudget;
